/**
 * Grade de multiplicadores de crédito por source.
 *
 * Exibe uma linha editável por source com o valor atual do multiplicador.
 * Salva via PUT /api/v1/admin/billing/multipliers/{source}.
 */
import { useState } from 'react';
import { Button } from '../../design-system/components/Button';
import { Card } from '../../design-system/components/Card';
import { TextField } from '../../design-system/components/TextField';
import { Spinner } from '../../design-system/components/Spinner';
import { ErrorState } from '../../shared/components/ErrorState';
import { EmptyState } from '../../shared/components/EmptyState';
import { useMultipliers, useUpdateMultiplier } from './hooks';
import type { MultiplierPublic } from './types';

interface MultiplierRowProps {
  item: MultiplierPublic;
}

function MultiplierRow({ item }: MultiplierRowProps) {
  const mutation = useUpdateMultiplier();
  const [value, setValue] = useState(String(item.multiplier));
  const [saved, setSaved] = useState(false);
  const [invalido, setInvalido] = useState(false);

  function handleSalvar() {
    setSaved(false);
    const multiplier = parseFloat(value);
    if (!(multiplier >= 0)) {
      setInvalido(true);
      return;
    }
    setInvalido(false);
    mutation.mutate(
      { source: item.source, payload: { multiplier } },
      { onSuccess: () => setSaved(true) },
    );
  }

  return (
    <tr>
      <td>{item.source}</td>
      <td>
        <TextField
          label={`Multiplicador de ${item.source}`}
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setSaved(false);
            setInvalido(false);
          }}
        />
      </td>
      <td>
        <div className="row row--sm-gap">
          <Button
            variant="secondary"
            onClick={handleSalvar}
            isLoading={mutation.isPending}
            disabled={mutation.isPending}
          >
            Salvar
          </Button>
          {saved && (
            <span className="label" role="status" aria-live="polite">
              Salvo
            </span>
          )}
          {invalido && (
            <span className="label" role="alert">
              Informe um número maior ou igual a zero
            </span>
          )}
          {mutation.isError && (
            <span className="label" role="alert">
              Erro ao salvar
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

export function MultipliersGrid() {
  const query = useMultipliers();

  if (query.isPending) {
    return (
      <div className="center">
        <Spinner label="Carregando multiplicadores" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        error={query.error}
        onRetry={() => query.refetch()}
        title="Não foi possível carregar os multiplicadores"
      />
    );
  }

  const items = query.data ?? [];

  return (
    <Card>
      <h2 className="dash-section__title">Multiplicadores de crédito</h2>
      <p className="muted">Fator aplicado ao consumo de créditos por source de transcrição/LLM.</p>
      {items.length === 0 ? (
        <EmptyState
          title="Nenhum multiplicador cadastrado"
          description="Cadastre multiplicadores via API ou seed de dados."
        />
      ) : (
        <div className="billing-admin__scroll">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Multiplicador</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <MultiplierRow key={item.source} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
